import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Alert, DatePicker, Empty, Input, Modal, Radio, Select, Table, Tag, message } from "antd";
import { ArrowLeftOutlined, ExportOutlined } from "@ant-design/icons";
import { ModalItem, ButtonItem, CheckItem, fmtFechaDia } from "../../../../Components/Items";

/**
 * Traspaso de empleados a otra empresa del usuario
 * (erp_backend/Controllers/Empleados/traspasoEmpleados.controllers.js).
 *
 * Paso 1: empresa destino y empleados (activos). Paso 2: vista previa, que sólo dice qué pasa con
 * los catálogos propios de la empresa y con la fecha de alta; los catálogos sin equivalente en
 * destino se eligen aquí. La ficha completa (documentos, biometría, créditos, etc.) viaja sola.
 *
 * Los candados viven en el backend: cada cambio de catálogo se vuelve a evaluar allá, y al
 * traspasar se evalúa una vez más con lo elegido.
 *
 * Es un `Table` de antd y no `TableItem` porque éste ignora `pagination` y `scroll`.
 *
 * Props: visible, onCerrar(huboCambios)
 */

const normalizar = (texto) =>
  String(texto ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const codigoTexto = (codigo) => (codigo !== null && codigo !== undefined ? String(codigo).padStart(4, "0") : "—");

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

const ESTADO_INICIAL = {
  paso: 1,
  empresaDestinoId: undefined,
  seleccionados: [],
  busqueda: "",
  mapeo: {},
  modoFecha: "conservar",
  fecha: null,
  deshabilitarOrigen: false,
  relojesDestinoIds: [],
  evaluacion: null,
  error: null,
};

class ModalTraspasoEmpleados extends Component {
  state = {
    cargando: false,
    evaluando: false,
    enviando: false,
    empresas: [],
    empleados: [],
    ...ESTADO_INICIAL,
  };
  // Descarta las respuestas de una evaluación vieja: se puede cambiar otro catálogo antes de que
  // regrese la primera.
  consultaVigente = 0;

  componentDidUpdate(prevProps) {
    // Se consulta en cada apertura: pudo haber altas, bajas o empresas nuevas desde la última vez.
    if (this.props.visible && !prevProps.visible) {
      this.setState({ ...ESTADO_INICIAL });
      this.consultarDatos();
    }
  }

  async consultarDatos() {
    this.setState({ cargando: true, empresas: [], empleados: [] });
    try {
      const res = await axios.post(
        "empleados/datosTraspaso",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      );
      if (res.data.success !== 2) {
        this.setState({ cargando: false });
        message.error(res.data.mensaje || "No se pudieron consultar los datos del traspaso", 8);
        return;
      }
      this.setState({ cargando: false, empresas: res.data.empresas || [], empleados: res.data.empleados || [] });
    } catch (e) {
      this.setState({ cargando: false });
      message.error("Error de conexión al consultar los datos del traspaso");
    }
  }

  cuerpo() {
    const { empresaDestinoId, seleccionados, mapeo, modoFecha, fecha, deshabilitarOrigen, relojesDestinoIds } = this.state;
    return {
      empresaId: this.props.empresaId,
      empresaDestinoId,
      empleadosIds: seleccionados,
      mapeo,
      // Fecha-día: viaja como texto y el backend la ancla al mediodía.
      fechaAlta: { modo: modoFecha, fecha: modoFecha === "fecha" && fecha ? fecha.format("YYYY-MM-DD") : null },
      deshabilitarOrigen,
      relojesDestinoIds,
    };
  }

  async evaluar() {
    const consulta = ++this.consultaVigente;
    this.setState({ evaluando: true, error: null });
    try {
      // La fecha no cambia nada de lo que se evalúa: se manda siempre como "conservar" para que
      // la vista previa no falle mientras se elige.
      const res = await axios.post(
        "empleados/evaluarTraspaso",
        { ...this.cuerpo(), fechaAlta: { modo: "conservar" } },
        { headers: this.props.headersToken }
      );
      if (consulta !== this.consultaVigente) return;
      if (res.data.success !== 2) {
        this.setState({ evaluando: false, error: res.data.mensaje || "No se pudo revisar el traspaso" });
        return;
      }
      this.setState({ evaluando: false, paso: 2, evaluacion: res.data });
    } catch (e) {
      if (consulta !== this.consultaVigente) return;
      this.setState({ evaluando: false, error: "Error de conexión al revisar el traspaso" });
    }
  }

  elegirCatalogo(clave, origenId, valor) {
    const mapeo = { ...this.state.mapeo, [clave]: { ...(this.state.mapeo[clave] || {}), [origenId]: valor } };
    this.setState({ mapeo }, () => this.evaluar());
  }

  cerrar(huboCambios) {
    if (this.state.enviando) return;
    this.props.onCerrar(huboCambios);
  }

  traspasar = async () => {
    this.setState({ enviando: true });
    try {
      const res = await axios.post("empleados/traspasarEmpleados", this.cuerpo(), { headers: this.props.headersToken });
      this.setState({ enviando: false });
      if (res.data.success !== 2) {
        message.error(res.data.mensaje || "No se pudo traspasar", 8);
        this.evaluar();
        return;
      }
      message.success(res.data.mensaje || "Traspaso terminado");
      this.mostrarResultado(res.data);
      this.props.onCerrar(true);
    } catch (e) {
      this.setState({ enviando: false });
      message.error("Error de conexión al traspasar");
    }
  };

  mostrarResultado(datos) {
    const resumen = datos.resumen || [];
    const ok = resumen.filter((r) => r.ok);
    const fallidos = resumen.filter((r) => !r.ok);
    const bloqueados = datos.bloqueados || [];
    Modal.info({
      title: "Traspaso terminado",
      width: 620,
      content: (
        <div>
          <p>{datos.mensaje}</p>
          {ok.length > 0 && (
            <ul>
              {ok.map((r) => (
                <li key={r.empleadoId}>
                  <b>{r.nombre}</b> · código {codigoTexto(r.codigo)}
                  {r.relojes?.length > 0 && ` · ${r.relojes.map((x) => `${x.reloj} (PIN ${x.pin})`).join(", ")}`}
                  {r.avisos?.length > 0 && (
                    <ul style={{ color: "#ad6800" }}>{r.avisos.map((a, i) => <li key={i}>{a}</li>)}</ul>
                  )}
                </li>
              ))}
            </ul>
          )}
          {(fallidos.length > 0 || bloqueados.length > 0) && (
            <>
              <p style={{ marginBottom: 4 }}>No se traspasaron:</p>
              <ul style={{ color: "#cf1322" }}>
                {fallidos.map((r) => <li key={r.empleadoId}><b>{r.nombre}</b>: {r.mensaje}</li>)}
                {bloqueados.map((e) => <li key={e._id}><b>{e.nombre}</b>: {e.bloqueos.join(" ")}</li>)}
              </ul>
            </>
          )}
        </div>
      ),
    });
  }

  filtrarPorBusqueda(filas) {
    const texto = normalizar(this.state.busqueda.trim());
    if (!texto) return filas;
    return filas.filter((e) =>
      [codigoTexto(e.codigo), e.nombre, e.rfc, e.departamento].some((campo) => normalizar(campo).includes(texto))
    );
  }

  renderPaso1() {
    const { empresas, empleados, empresaDestinoId, seleccionados, busqueda, error, evaluando } = this.state;
    const columnas = [
      { title: "Código", key: "codigo", width: 80, render: (_, r) => codigoTexto(r.codigo) },
      { title: "Nombre", dataIndex: "nombre", key: "nombre" },
      { title: "RFC", dataIndex: "rfc", key: "rfc", width: 140 },
      { title: "Departamento", dataIndex: "departamento", key: "departamento", width: 160 },
    ];

    return (
      <>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Empresa destino</div>
        <Select
          showSearch
          style={{ width: "100%", marginBottom: 12 }}
          placeholder={empresas.length ? "Elige la empresa a la que pasan" : "No tienes acceso a otra empresa"}
          value={empresaDestinoId}
          optionFilterProp="label"
          // Otra empresa, otros catálogos: lo elegido para la anterior ya no aplica.
          onChange={(valor) => this.setState({ empresaDestinoId: valor, mapeo: {}, evaluacion: null, error: null })}
          options={empresas.map((e) => ({
            value: e._id,
            label: `${e.nombre}${e.rfc ? ` (${e.rfc})` : ""}${e.vencida ? " · suscripción vencida" : ""}`,
            disabled: e.vencida,
          }))}
        />

        <Input.Search
          allowClear
          style={{ marginBottom: 10 }}
          placeholder="Buscar por código, nombre, RFC o departamento"
          value={busqueda}
          onChange={(e) => this.setState({ busqueda: e.target.value })}
        />
        <div style={{ overflowX: "auto" }}>
          <Table
            size="small"
            rowKey={(r) => String(r._id)}
            dataSource={this.filtrarPorBusqueda(empleados)}
            columns={columnas}
            pagination={{ pageSize: 50, showSizeChanger: false }}
            scroll={{ x: 640, y: 320 }}
            locale={{ emptyText: <Empty description="No hay empleados activos con ese filtro" /> }}
            rowSelection={{
              selectedRowKeys: seleccionados,
              preserveSelectedRowKeys: true,
              selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
              onChange: (llaves) => this.setState({ seleccionados: llaves, mapeo: {}, evaluacion: null, error: null }),
            }}
          />
        </div>

        {error && <Alert type="error" showIcon style={{ marginTop: 10 }} message={error} />}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, marginBottom: 10 }}>
          <ButtonItem htmlType="button" blanco onClick={() => this.cerrar(false)}>
            Cancelar
          </ButtonItem>
          <ButtonItem
            htmlType="button"
            type="primary"
            loading={evaluando}
            disabled={!empresaDestinoId || !seleccionados.length || evaluando}
            onClick={() => this.evaluar()}
          >
            {`Revisar traspaso (${seleccionados.length})`}
          </ButtonItem>
        </div>
      </>
    );
  }

  renderDestinoCatalogo(catalogo, fila) {
    if (fila.resolucion === "mismo") return <Tag color="green">{fila.destinoNombre}</Tag>;
    if (fila.resolucion === "lote") return <Tag color="blue">{fila.destinoNombre}</Tag>;
    if (catalogo.soloLectura) return <Tag>Se queda vacío</Tag>;

    const opciones = [
      ...(catalogo.creable ? [{ value: "crear", label: `Crear «${fila.origenNombre}»` }] : []),
      { value: "vacio", label: "Dejar vacío" },
      ...catalogo.opciones.map((o) => ({ value: o._id, label: o.nombre })),
    ];
    return (
      <Select
        showSearch
        size="small"
        style={{ width: "100%" }}
        optionFilterProp="label"
        value={fila.resolucion === "elegido" ? fila.destinoId : fila.resolucion}
        onChange={(valor) => this.elegirCatalogo(catalogo.clave, fila.origenId, valor)}
        options={opciones}
      />
    );
  }

  renderCatalogos(evaluacion) {
    const filas = [];
    for (const c of evaluacion.catalogos) {
      c.filas.forEach((f, i) => filas.push({ ...f, key: `${c.clave}|${f.origenId}`, catalogo: c, primera: i === 0 }));
    }
    if (!filas.length) {
      return <p style={{ color: "#8c8c8c" }}>Los empleados elegidos no usan catálogos propios de la empresa.</p>;
    }
    const sinEquivalente = filas.filter((f) => !f.catalogo.soloLectura && f.resolucion !== "mismo").length;

    const columnas = [
      {
        title: "Catálogo",
        key: "catalogo",
        width: 170,
        render: (_, f) => (f.primera ? <b>{f.catalogo.etiqueta}</b> : null),
      },
      { title: "En esta empresa", dataIndex: "origenNombre", key: "origen" },
      { title: "Empleados", dataIndex: "empleados", key: "empleados", width: 90, align: "right" },
      {
        title: `En ${evaluacion.empresaDestino.nombre}`,
        key: "destino",
        width: 280,
        render: (_, f) => this.renderDestinoCatalogo(f.catalogo, f),
      },
    ];

    return (
      <>
        <p style={{ marginBottom: 6, color: "#595959" }}>
          Los catálogos del SAT pasan igual. Los de la empresa se buscan por nombre en la destino
          {sinEquivalente > 0 ? `; ${plural(sinEquivalente, "no tiene", "no tienen")} equivalente: elige qué usar.` : "."}
        </p>
        <div style={{ overflowX: "auto" }}>
          <Table
            size="small"
            rowKey="key"
            dataSource={filas}
            columns={columnas}
            pagination={false}
            scroll={{ x: 700, y: 300 }}
          />
        </div>
      </>
    );
  }

  renderFechaAlta(evaluacion) {
    const { modoFecha, fecha } = this.state;
    const filas = evaluacion.fechasAlta.map((f) => ({
      ...f,
      key: String(f.empleadoId),
      usadaTexto: modoFecha === "fecha" ? (fecha ? fecha.format("DD/MM/YYYY") : "Elige la fecha") : fmtFechaDia(f.original, "Sin fecha"),
    }));

    return (
      <>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 8 }}>
          <Radio.Group
            value={modoFecha}
            onChange={(e) => this.setState({ modoFecha: e.target.value })}
            options={[
              { label: "Conservar la de cada empleado", value: "conservar" },
              { label: "Usar esta fecha para todos", value: "fecha" },
            ]}
          />
          {modoFecha === "fecha" && (
            <DatePicker
              format="DD/MM/YYYY"
              value={fecha}
              placeholder="Fecha de alta"
              onChange={(valor) => this.setState({ fecha: valor })}
            />
          )}
        </div>
        <p style={{ marginBottom: 6, color: "#595959" }}>
          {modoFecha === "conservar"
            ? "Se reconoce la antigüedad: viajan su fecha de alta, sus reingresos y su saldo de vacaciones."
            : "La relación laboral empieza de cero en la destino: no se lleva el saldo de vacaciones."}
        </p>
        <Table
          size="small"
          rowKey="key"
          dataSource={filas}
          pagination={false}
          scroll={{ y: 200 }}
          columns={[
            { title: "Empleado", dataIndex: "nombre", key: "nombre" },
            { title: "En esta empresa", key: "original", width: 150, render: (_, f) => fmtFechaDia(f.original, "Sin fecha") },
            { title: `En ${evaluacion.empresaDestino.nombre}`, dataIndex: "usadaTexto", key: "usada", width: 190 },
          ]}
        />
      </>
    );
  }

  renderPaso2() {
    const { evaluacion, evaluando, enviando, error, modoFecha, fecha, deshabilitarOrigen, relojesDestinoIds } = this.state;
    const bloqueados = evaluacion.empleados.filter((e) => e.bloqueos.length);
    const aTraspasar = evaluacion.empleados.length - bloqueados.length;
    const codigosCambian = evaluacion.empleados.filter(
      (e) => !e.bloqueos.length && e.codigoOrigen !== null && e.codigoOrigen !== e.codigoDestino
    );
    const puedeTraspasar = !evaluando && aTraspasar > 0 && (modoFecha === "conservar" || !!fecha);
    const seccion = (titulo) => <div style={{ fontWeight: 600, fontSize: 15, margin: "14px 0 6px" }}>{titulo}</div>;

    return (
      <>
        {error && <Alert type="error" showIcon style={{ marginBottom: 10 }} message={error} />}
        {bloqueados.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 10 }}
            message={`No se traspasan (${bloqueados.length})`}
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {bloqueados.map((e) => <li key={e._id}><b>{e.nombre}</b>: {e.bloqueos.join(" ")}</li>)}
              </ul>
            }
          />
        )}

        {seccion("Catálogos de la empresa")}
        {this.renderCatalogos(evaluacion)}

        {seccion("Fecha de alta")}
        {this.renderFechaAlta(evaluacion)}

        {codigosCambian.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 10 }}
            message={`Código ocupado en ${evaluacion.empresaDestino.nombre}: ${codigosCambian
              .map((e) => `${e.nombre} (${codigoTexto(e.codigoOrigen)} → ${codigoTexto(e.codigoDestino)})`)
              .join(", ")}.`}
          />
        )}

        {seccion("Al traspasar")}
        <div style={{ marginBottom: 8 }}>
          <CheckItem checked={deshabilitarOrigen} onChange={(v) => this.setState({ deshabilitarOrigen: v })}>
            {`Deshabilitarlos en ${this.props.empresaNombre || "esta empresa"} y quitarlos de sus relojes checadores`}
          </CheckItem>
        </div>
        <Select
          mode="multiple"
          allowClear
          style={{ width: "100%" }}
          placeholder={
            evaluacion.relojesDestino.length
              ? "Relojes checadores en la destino (opcional)"
              : "La empresa destino no tiene relojes checadores"
          }
          disabled={!evaluacion.relojesDestino.length}
          value={relojesDestinoIds}
          optionFilterProp="label"
          onChange={(valores) => this.setState({ relojesDestinoIds: valores })}
          options={evaluacion.relojesDestino.map((r) => ({ value: r._id, label: `${r.nombre}${r.sn ? ` (${r.sn})` : ""}` }))}
        />
        {relojesDestinoIds.length > 0 && (
          <p style={{ marginTop: 4, color: "#595959" }}>
            Se usa el mismo PIN si está libre en ese reloj (si no, el siguiente) y se le mandan sus huellas, rostros y contraseña.
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 14, marginBottom: 10 }}>
          <ButtonItem htmlType="button" blanco icon={<ArrowLeftOutlined />} disabled={enviando} onClick={() => this.setState({ paso: 1, error: null })}>
            Regresar
          </ButtonItem>
          <div style={{ display: "flex", gap: 8 }}>
            <ButtonItem htmlType="button" blanco onClick={() => this.cerrar(false)}>
              Cancelar
            </ButtonItem>
            <ButtonItem
              htmlType="button"
              type="primary"
              icon={<ExportOutlined />}
              loading={enviando}
              disabled={!puedeTraspasar}
              onClick={this.traspasar}
            >
              {aTraspasar === 1 ? "Traspasar 1 empleado" : `Traspasar ${aTraspasar} empleados`}
            </ButtonItem>
          </div>
        </div>
      </>
    );
  }

  render() {
    const { cargando, evaluando, enviando, paso, evaluacion } = this.state;
    return (
      <ModalItem
        title={paso === 2 && evaluacion ? `Traspasar a ${evaluacion.empresaDestino.nombre}` : "Traspasar empleados a otra empresa"}
        visible={this.props.visible}
        spinning={cargando || evaluando || enviando}
        onCancel={() => this.cerrar(false)}
        paddingTop
        width={window.innerWidth > 1000 ? 900 : "95%"}
        destroyOnClose
      >
        {paso === 2 && evaluacion ? this.renderPaso2() : this.renderPaso1()}
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresaNombre: state.empresa.nombre,
});

export default connect(mapStateToProps)(ModalTraspasoEmpleados);
